import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BookingDTO, OrderDTO, OrderItemDTO } from '@lkp/parking-sdk';
import { Subject, takeUntil } from 'rxjs';
import {
  CheckoutOrderPayLater,
  DeleteOrder,
  DeleteOrderItem,
  ResetOrderState,
} from 'src/app/core/states/orders/orders.action';
import { OrdersState } from 'src/app/core/states/orders/orders.state';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import { UsersState } from 'src/app/core/states/users/users.state';
import { normalizeErrorMessage } from 'src/app/core/utils/error-normalize';

@Component({
    selector: 'app-order-info',
    templateUrl: './order-info.component.html',
    styleUrls: ['./order-info.component.scss'],
    standalone: false
})
export class OrderInfoComponent implements OnInit, OnDestroy {
  currentOrder?: OrderDTO;
  newOrder: boolean = true;
  isDriverMode: boolean = false;
  finishOrderLoading: boolean = false;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly store: Store,
    public router: Router,
    readonly dialog: MatDialog
  ) {
    this.store
      .selectOnce(OrdersState.newOrder)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((newOrder) => {
        this.newOrder = !newOrder;
      });

    this.store
      .selectOnce(UsersState.driverMode)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isDriverMode) => {
        this.isDriverMode = isDriverMode;
      });
  }

  ngOnInit(): void {
    this.store
      .select(OrdersState.order)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) => {
        this.currentOrder = order;
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  removeOrderItem(item: OrderItemDTO) {
    if (this.currentOrder?.orderId)
      this.store.dispatch(
        new DeleteOrderItem(this.currentOrder.orderId, item.orderItemId!)
      );
  }

  removeOrder() {
    const focusedElement = document.activeElement as HTMLElement;
    focusedElement?.blur();
    const confDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Avbryt order',
        message: ['Är du säker på att du vill avbryta pågående order?'],
        yesBtnText: 'Ja',
        noBtnText: 'Nej',
      },
    });

    confDialog
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          if (!this.newOrder) {
            this.store.dispatch(new ResetOrderState());
          } else if (this.currentOrder?.orderId) {
            this.store.dispatch(new DeleteOrder(this.currentOrder.orderId));
          }
        }
      });
  }

  editBooking(booking: BookingDTO) {
    this.router.navigate(['redigera-bokning', booking.bookingId]);
  }

  /**
   * Handles the "Slutför"/"Hantera betalning" button. In driver mode, the
   * payment method is always "Betala senare" - check the order out as
   * pay-later before navigating so the driver lands directly on the
   * checkout page's "Order genomförd" confirmation instead of being shown
   * payment-method selection.
   */
  finishOrder(): void {
    if (this.isDriverMode && this.currentOrder?.orderId) {
      this.finishOrderLoading = true;
      this.store
        .dispatch(new CheckoutOrderPayLater(this.currentOrder.orderId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: () => {
            this.router.navigate(['hantera-betalning']);
          },
          error: (error) => {
            this.finishOrderLoading = false;
            console.error(
              'Error checking out order as pay later in driver mode: ',
              error
            );
            this.dialog.open(ErrorDialogComponent, {
              data: {
                title: 'Det gick inte att slutföra bokningen',
                message: [normalizeErrorMessage(error)],
              },
            });
          },
        });
    } else {
      this.router.navigate(['hantera-betalning']);
    }
  }

  render(): boolean {
    return (
      (!this.store.selectSnapshot(OrdersState.checkingOut) &&
        this.currentOrder &&
        this.currentOrder?.orderId &&
        this.currentOrder.orderItems &&
        this.currentOrder.orderItems.length > 0 &&
        this.router.url !== '/hantera-betalning' &&
        this.router.url !== '/aterbetalning' &&
        !this.router.url.includes('order')) ||
      false
    );
  }
}
